import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    useTheme,
    Typography,
    Card
} from '@mui/material';
import { useThemeColors } from '../hooks/useThemeColors';

// Hook personalizzato per i colori delle tabelle standardizzati (backward compatibility)
export const useTableColors = () => {
    const { table } = useThemeColors();
    return {
        ...table,
        // Mapping per compatibilità con vecchi nomi
        container: table.rowEven, // Assumendo che container sia simile a rowEven
        headerText: table.headerText,
        rowEven: table.rowEven,
        rowOdd: table.rowOdd,
        hover: table.hover,
        border: table.border,
        text: table.text
    };
};

// Componente TableContainer standardizzato
export const StandardTableContainer = ({ 
    children, 
    sx = {}, 
    elevation = 1,
    ...props 
}) => {
    const tableColors = useTableColors();
    
    return (
        <TableContainer
            component={Paper}
            elevation={elevation}
            sx={{
                backgroundColor: tableColors.container,
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: tableColors.shadow,
                mb: 2,
                ...sx
            }}
            {...props}
        >
            {children}
        </TableContainer>
    );
};

// Componente Table standardizzato
export const StandardTable = ({ 
    children, 
    size = "medium",
    sx = {},
    ...props 
}) => {
    return (
        <Table
            size={size}
            sx={{
                minWidth: { xs: 300, sm: 650 },
                '& .MuiTableCell-root': {
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    padding: { xs: '8px', sm: '16px' }
                },
                ...sx
            }}
            {...props}
        >
            {children}
        </Table>
    );
};

// Componente TableHead standardizzato
export const StandardTableHead = ({ 
    children, 
    sx = {},
    ...props 
}) => {
    const tableColors = useTableColors();
    
    return (
        <TableHead
            sx={{
                backgroundColor: tableColors.header,
                '& .MuiTableCell-root': {
                    backgroundColor: tableColors.header,
                    color: tableColors.headerText,
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    borderBottom: `2px solid ${tableColors.border}`,
                    padding: { xs: '12px 8px', sm: '16px' }
                },
                ...sx
            }}
            {...props}
        >
            {children}
        </TableHead>
    );
};

// Componente TableRow standardizzato per il body
export const StandardTableRow = ({ 
    children, 
    index,
    hover = true,
    sx = {},
    ...props 
}) => {
    const tableColors = useTableColors();
    
    return (
        <TableRow
            hover={hover}
            sx={{
                backgroundColor: index % 2 === 0 ? tableColors.rowEven : tableColors.rowOdd,
                '&:hover': hover ? {
                    backgroundColor: tableColors.hover,
                } : {},
                '& .MuiTableCell-root': {
                    color: tableColors.text,
                    borderBottom: `1px solid ${tableColors.border}`,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px', sm: '16px' }
                },
                ...sx
            }}
            {...props}
        >
            {children}
        </TableRow>
    );
};

// Componente TableCell standardizzato per header
export const StandardHeaderCell = ({ 
    children, 
    align = "left",
    sortable = false,
    sx = {},
    ...props 
}) => {
    const tableColors = useTableColors();
    
    return (
        <TableCell
            align={align}
            sx={{
                backgroundColor: tableColors.header,
                color: tableColors.headerText,
                fontWeight: 'bold',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                borderBottom: `2px solid ${tableColors.border}`,
                padding: { xs: '12px 8px', sm: '16px' },
                cursor: sortable ? 'pointer' : 'default',
                '&:hover': sortable ? {
                    backgroundColor: tableColors.hover,
                } : {},
                ...sx
            }}
            {...props}
        >
            {children}
        </TableCell>
    );
};

// Componente TableCell standardizzato per body
export const StandardBodyCell = ({ 
    children, 
    align = "left",
    sx = {},
    ...props 
}) => {
    const tableColors = useTableColors();
    
    return (
        <TableCell
            align={align}
            sx={{
                color: tableColors.text,
                borderBottom: `1px solid ${tableColors.border}`,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                padding: { xs: '8px', sm: '16px' },
                ...sx
            }}
            {...props}
        >
            {children}
        </TableCell>
    );
};

// Componente completo StandardTable già assemblato
export const StandardDataTable = ({ 
    headers = [],
    data = [],
    renderRow,
    title,
    actions,
    loading = false,
    emptyMessage = "Nessun dato disponibile",
    sx = {},
    tableProps = {},
    ...props 
}) => {
    const tableColors = useTableColors();
    
    return (
        <Card sx={{ p: 0, overflow: 'hidden', ...sx }} {...props}>
            {title && (
                <Box sx={{ p: 2, backgroundColor: tableColors.header }}>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            color: tableColors.headerText,
                            fontWeight: 'bold'
                        }}
                    >
                        {title}
                    </Typography>
                    {actions && (
                        <Box sx={{ mt: 1 }}>
                            {actions}
                        </Box>
                    )}
                </Box>
            )}
            
            <StandardTableContainer sx={{ boxShadow: 'none' }}>
                <StandardTable {...tableProps}>
                    <StandardTableHead>
                        <TableRow>
                            {headers.map((header, index) => (
                                <StandardHeaderCell 
                                    key={index}
                                    align={header.align || 'left'}
                                    sortable={header.sortable}
                                >
                                    {header.label}
                                </StandardHeaderCell>
                            ))}
                        </TableRow>
                    </StandardTableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <StandardBodyCell 
                                    colSpan={headers.length} 
                                    align="center"
                                    sx={{ py: 4 }}
                                >
                                    Caricamento...
                                </StandardBodyCell>
                            </TableRow>
                        ) : data.length === 0 ? (
                            <TableRow>
                                <StandardBodyCell 
                                    colSpan={headers.length} 
                                    align="center"
                                    sx={{ py: 4, fontStyle: 'italic' }}
                                >
                                    {emptyMessage}
                                </StandardBodyCell>
                            </TableRow>
                        ) : (
                            data.map((row, index) => renderRow ? renderRow(row, index) : (
                                <StandardTableRow key={index} index={index}>
                                    {headers.map((header, cellIndex) => (
                                        <StandardBodyCell 
                                            key={cellIndex}
                                            align={header.align || 'left'}
                                        >
                                            {row[header.key] || '-'}
                                        </StandardBodyCell>
                                    ))}
                                </StandardTableRow>
                            ))
                        )}
                    </TableBody>
                </StandardTable>
            </StandardTableContainer>
        </Card>
    );
};

// Componente per tabelle responsive che si trasformano in card su mobile
export const ResponsiveDataTable = ({ 
    headers = [],
    data = [],
    renderMobileCard,
    breakpoint = 'md',
    ...props 
}) => {
    const theme = useTheme();
    const tableColors = useTableColors();
    
    return (
        <Box>
            {/* Versione desktop/tablet */}
            <Box sx={{ display: { xs: 'none', [breakpoint]: 'block' } }}>
                <StandardDataTable 
                    headers={headers}
                    data={data}
                    {...props}
                />
            </Box>
            
            {/* Versione mobile - cards */}
            <Box sx={{ display: { xs: 'block', [breakpoint]: 'none' } }}>
                {data.length === 0 ? (
                    <Card sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="textSecondary">
                            {props.emptyMessage || "Nessun dato disponibile"}
                        </Typography>
                    </Card>
                ) : (
                    data.map((row, index) => (
                        <Card 
                            key={index} 
                            sx={{ 
                                mb: 2, 
                                p: 2,
                                backgroundColor: index % 2 === 0 ? tableColors.rowEven : tableColors.rowOdd
                            }}
                        >
                            {renderMobileCard ? renderMobileCard(row, index) : (
                                <Box>
                                    {headers.map((header, cellIndex) => (
                                        <Box 
                                            key={cellIndex}
                                            sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between',
                                                mb: 1,
                                                pb: 1,
                                                borderBottom: cellIndex < headers.length - 1 ? 
                                                    `1px solid ${tableColors.border}` : 'none'
                                            }}
                                        >
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontWeight: 'bold',
                                                    color: tableColors.headerText,
                                                    minWidth: '40%'
                                                }}
                                            >
                                                {header.label}:
                                            </Typography>
                                            <Typography 
                                                variant="body2"
                                                sx={{ 
                                                    color: tableColors.text,
                                                    textAlign: 'right',
                                                    flex: 1,
                                                    ml: 2
                                                }}
                                            >
                                                {row[header.key] || '-'}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Card>
                    ))
                )}
            </Box>
        </Box>
    );
};

export default StandardDataTable;